import type { CollectionsTab, InvoiceList, InvoiceReason } from "./types";
import type { PaymentList } from "@/features/reports/appointments/types";
import type {
  ReportFilterOption,
  ReportSeriesDefinition,
} from "@/features/reports/common/types";
import type { SortDescriptor } from "@heroui/react";

import { REPORT_FILTER_ANY } from "@/features/reports/common/constants/list-filters";
import { SERIES_COLOR } from "@/utils/chart-color";
import { formatCompactPrice, formatPrice } from "@/utils/number";

export const COLLECTIONS_REPORT_PATH = "/reports/collections";
export const CASH_IN_PARTS_TITLE = "Advance vs direct";

export const COLLECTIONS_CARD = {
  CASH_IN: "collections_cash_in",
  PARTS: "collections_parts",
  FIGURES: "collections_figures",
  PATIENTS_FIGURES: "collections_patients_figures",
  PAYMENTS: "collections_payments",
  INVOICES: "collections_invoices",
} as const;

/** Each is its own page, `/reports/collections/<id>`, in the sidebar's Collections section; the first opens by default. */
export const COLLECTIONS_TABS: {
  id: CollectionsTab;
  label: string;
  icon: string;
}[] = [
  { id: "cash-in", label: "Cash in", icon: "solar:wallet-money-linear" },
  {
    id: "verification",
    label: "Needs verification",
    icon: "solar:shield-check-linear",
  },
];

export const COLLECTIONS_TAB_IDS = COLLECTIONS_TABS.map(({ id }) => id);

export const COLLECTIONS_SKELETON_ROWS = 6;

/** Collected, Paid in as advances and Paid directly. */
export const MONEY_TILE_COUNT = 3;

/** Stacked bottom-up: advances, then direct. */
export const CASH_IN_SERIES: ReportSeriesDefinition[] = [
  {
    key: "advances",
    label: "Paid in as advances",
    color: SERIES_COLOR.cyan,
    format: formatPrice,
    axisFormat: formatCompactPrice,
  },
  {
    key: "direct",
    label: "Paid directly",
    color: SERIES_COLOR.blue,
    format: formatPrice,
    axisFormat: formatCompactPrice,
  },
];

export const INVOICE_REASON_LABEL: Record<InvoiceReason, string> = {
  outstanding: "Outstanding",
  no_receipt: "No receipt on file",
};

export const INVOICE_CHECK_ISSUE_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Outstanding or no receipt" },
  { key: "outstanding", label: INVOICE_REASON_LABEL.outstanding },
  { key: "no_receipt", label: INVOICE_REASON_LABEL.no_receipt },
];

export const INVOICE_CHECK_LIST: Record<string, InvoiceList> = {
  [REPORT_FILTER_ANY]: "verification",
  outstanding: "outstanding",
  no_receipt: "no_receipt",
};

export const INVOICE_CHECK_DEFAULT_SORT: SortDescriptor = {
  column: "issued",
  direction: "descending",
};

export const NO_AMOUNTS_NOTE =
  "Amounts are hidden for your role. An admin can turn on Can see amounts for it.";

/** The page reads the appointments report's figures too, so refresh reloads every report key. */
export const COLLECTIONS_REFRESH_KEY = ["REPORTS"] as const;

/** The Payments received list's own dates, from its picker. */
export const RECEIVED_PARAMS = ["paidFrom", "paidTo"] as const;

export const RECEIPT_LEDGER_TYPE_OPTIONS: ReportFilterOption[] = [
  { key: REPORT_FILTER_ANY, label: "Advances and receipts" },
  { key: "advance", label: "Advances" },
  { key: "receipt", label: "Receipts" },
];

export const RECEIPT_LEDGER_LIST: Record<string, PaymentList> = {
  [REPORT_FILTER_ANY]: "collected",
  advance: "advances",
  receipt: "direct",
};

export const RECEIPT_LEDGER_DEFAULT_SORT: SortDescriptor = {
  column: "received",
  direction: "descending",
};
